import * as satellite from 'satellite.js';

export interface TleRecord {
  line1: string;
  line2: string;
  name?: string;
  norad_cat_id?: number;
  satelliteId?: number;
}

function extractTleArray(json: unknown): TleRecord[] {
  if (Array.isArray(json)) return json as TleRecord[];
  if (json && typeof json === 'object') {
    const o = json as Record<string, unknown>;
    for (const key of ['member', 'data', 'items', 'hydra:member', 'results'] as const) {
      const v = o[key];
      if (Array.isArray(v)) return v as TleRecord[];
    }
  }
  return [];
}

export async function fetchAllTleMembers(): Promise<TleRecord[]> {
  const url = new URL(`/api/tle/celestrak/active`, window.location.origin).toString();
  const res = await fetch(url);
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `TLE active list failed: ${res.status}`);
  }
  const data = (await res.json()) as Record<string, unknown>;
  return extractTleArray(data);
}

function propagateLonLat(line1: string, line2: string, date: Date): [number, number] | null {
  try {
    const satrec = satellite.twoline2satrec(line1, line2);
    const pv = satellite.propagate(satrec, date);
    const pos = pv?.position;
    if (!pos || typeof pos === 'boolean') return null;
    const gmst = satellite.gstime(date);
    const gd = satellite.eciToGeodetic(pos, gmst);
    const lat = satellite.degreesLat(gd.latitude);
    const lon = satellite.degreesLong(gd.longitude);
    return [lon, lat];
  } catch {
    return null;
  }
}

/** Propagate using an existing satrec (avoids re-parsing TLE every step). */
function propagateLonLatFromSatrec(satrec: satellite.SatRec, date: Date): [number, number] | null {
  try {
    const pv = satellite.propagate(satrec, date);
    const pos = pv?.position;
    if (!pos || typeof pos === 'boolean') return null;
    const gmst = satellite.gstime(date);
    const gd = satellite.eciToGeodetic(pos, gmst);
    const lat = satellite.degreesLat(gd.latitude);
    const lon = satellite.degreesLong(gd.longitude);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
    return [lon, lat];
  } catch {
    return null;
  }
}

/**
 * Propagate across the full time window into a single coordinate array.
 * Skips individual failed samples but does NOT break into segments —
 * a single null in the middle won't fragment the visible track.
 */
function propagateAllPoints(
  satrec: satellite.SatRec,
  start: Date,
  durationMs: number,
  stepMs: number
): [number, number][] {
  if (!Number.isFinite(durationMs) || durationMs <= 0) return [];
  if (!Number.isFinite(stepMs) || stepMs <= 0) return [];
  const coords: [number, number][] = [];
  for (let t = 0; t <= durationMs; t += stepMs) {
    const d = new Date(start.getTime() + t);
    const ll = propagateLonLatFromSatrec(satrec, d);
    if (ll) coords.push(ll);
  }
  return coords;
}

/**
 * Interpolate the latitude at which two points cross the ±180° meridian
 * and return edge coordinates on both sides.
 */
function antimeridianEdgePoints(
  a: [number, number],
  b: [number, number]
): { exit: [number, number]; enter: [number, number] } {
  const [lonA, latA] = a;
  const [lonB, latB] = b;

  // Unwrap B relative to A so the crossing is monotonic
  let bLon = lonB;
  if (bLon - lonA > 180) bLon -= 360;
  else if (lonA - bLon > 180) bLon += 360;

  const boundary = lonA > 0 ? 180 : -180;
  const dLon = bLon - lonA;
  const frac = dLon !== 0 ? (boundary - lonA) / dLon : 0.5;
  const latMid = latA + frac * (latB - latA);

  return {
    exit: [boundary, latMid],
    enter: [-boundary, latMid],
  };
}

/**
 * Compute the great-circle distance (in degrees) between two lon/lat points.
 * Used to distinguish real large moves from coordinate-system artefacts.
 */
function greatCircleDegrees(a: [number, number], b: [number, number]): number {
  const toRad = Math.PI / 180;
  const lat1 = a[1] * toRad;
  const lat2 = b[1] * toRad;
  const dLat = (b[1] - a[1]) * toRad;
  const dLon = (b[0] - a[0]) * toRad;
  const sinHalfDLat = Math.sin(dLat / 2);
  const sinHalfDLon = Math.sin(dLon / 2);
  const h = sinHalfDLat * sinHalfDLat +
            Math.cos(lat1) * Math.cos(lat2) * sinHalfDLon * sinHalfDLon;
  return (2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))) / toRad;
}

/**
 * Decide whether two consecutive samples should be connected or
 * whether a segment break is needed.
 *
 * Three cases cause a break:
 *  1. True antimeridian crossing — raw |Δlon| > 270°.
 *  2. Polar "longitude whip" — both points are above |lat| 70° and the
 *     raw |Δlon| > 90°.  Near the poles, longitude is almost undefined
 *     and can swing 180° in a single step even though the satellite
 *     barely moved.  Drawing a flat-map line between those points
 *     creates the big arcs / loops seen in the screenshot.
 *  3. The great-circle distance between consecutive samples exceeds
 *     what's physically possible for one step — a safety net for any
 *     other edge cases (e.g. re-entry, bad TLE).
 *
 * For case 1 we insert interpolated edge points so the line meets the
 * map edge cleanly.  For cases 2 & 3 we just start a new segment.
 */
function splitSegments(coords: [number, number][], stepMs: number): [number, number][][] {
  if (coords.length < 2) return coords.length === 1 ? [coords] : [];

  const ANTIMERIDIAN_THRESHOLD = 270; // degrees — only true ±180 wraps
  const POLAR_LAT = 70;              // degrees — latitude above which lon is unstable
  const POLAR_LON_JUMP = 90;         // degrees — max plausible lon change near pole

  // Max plausible ground-track speed: ~8 km/s → in degrees ≈ 0.072°/s.
  // For a 20s step that's ~1.4° per step; use 5° as generous safety margin.
  const MAX_GC_PER_STEP = Math.max(5, (stepMs / 1000) * 0.1);

  const segments: [number, number][][] = [];
  let current: [number, number][] = [coords[0]];

  for (let i = 1; i < coords.length; i++) {
    const prev = coords[i - 1];
    const curr = coords[i];
    const dLon = Math.abs(curr[0] - prev[0]);

    // Case 1: antimeridian crossing
    if (dLon > ANTIMERIDIAN_THRESHOLD) {
      const { exit, enter } = antimeridianEdgePoints(prev, curr);
      current.push(exit);
      if (current.length >= 2) segments.push(current);
      current = [enter, curr];
      continue;
    }

    // Case 2: polar longitude whip
    if (
      (Math.abs(prev[1]) > POLAR_LAT || Math.abs(curr[1]) > POLAR_LAT) &&
      dLon > POLAR_LON_JUMP
    ) {
      if (current.length >= 2) segments.push(current);
      current = [curr];
      continue;
    }

    // Case 3: implausibly large great-circle jump
    if (greatCircleDegrees(prev, curr) > MAX_GC_PER_STEP) {
      if (current.length >= 2) segments.push(current);
      current = [curr];
      continue;
    }

    current.push(curr);
  }
  if (current.length >= 2) segments.push(current);
  return segments;
}

export interface GroundTrackOptions {
  /** Total window length (default ~100 min ≈ 1.1 LEO orbits). */
  durationMs?: number;
  /** Sample interval (default 20s for smooth rendering). */
  stepMs?: number;
  /** Cap how many satellites get a track (default 40). */
  maxSatellites?: number;
}

const MAX_GROUND_TRACK_SAMPLES = 5000;
/**
 * One satellite → one or more LineString features.
 *
 * The `start` parameter is treated as the satellite's "current" time.
 * The track is drawn from (start − duration/2) to (start + duration/2)
 * so the satellite's position at `start` falls in the **middle** of the
 * track, not at the beginning.  This ensures the dot and line overlap.
 *
 * Strategy:
 *  1. Propagate the full time window into one flat coordinate array,
 *     silently skipping any sample where SGP4 returns null.
 *  2. Split at antimeridian crossings, polar longitude whips, and
 *     implausibly large jumps so lines never arc across the map.
 *  3. Each sub-segment becomes its own LineString with coords in [-180, 180].
 */
export function tleRecordToGroundTrackFeatures(
  record: TleRecord,
  start = new Date(),
  options: GroundTrackOptions = {}
): GeoJSON.Feature[] {
  if (!record.line1 || !record.line2) return [];

  let satrec: satellite.SatRec;
  try {
    satrec = satellite.twoline2satrec(record.line1, record.line2);
  } catch {
    return [];
  }

  // ── Detect orbital regime from TLE mean motion ──
  // Mean motion (revs/day) is in columns 53-63 of TLE line 2.
  // LEO ≈ 14–16 rev/day, MEO ≈ 2–6, GEO ≈ 1.0.
  let meanMotion = 15; // default LEO assumption
  try {
    meanMotion = parseFloat(record.line2.substring(52, 63).trim()) || 15;
  } catch {
    // keep default
  }

  const orbitalPeriodMs = (24 * 60 * 60 * 1000) / meanMotion; // ms per orbit

  // For GEO/HEO (period > 6h), show a full sidereal day (24h) so the
  // tiny figure-eight pattern is visible.  For MEO (period 2-6h) show
  // 1.5 orbits.  For LEO stick with the default ~100 min.
  let durationMs: number;
  let stepMs: number;

  if (options.durationMs != null) {
    durationMs = options.durationMs;
  } else if (orbitalPeriodMs > 6 * 60 * 60 * 1000) {
    // GEO / HEO: show 24 hours
    durationMs = 24 * 60 * 60 * 1000;
  } else if (orbitalPeriodMs > 2 * 60 * 60 * 1000) {
    // MEO: show 1.5 orbits
    durationMs = orbitalPeriodMs * 1.5;
  } else {
    // LEO: ~100 minutes
    durationMs = 100 * 60 * 1000;
  }

  if (options.stepMs != null) {
    stepMs = options.stepMs;
  } else if (orbitalPeriodMs > 6 * 60 * 60 * 1000) {
    // GEO: 2-minute steps (24h / 2min = 720 points — fine for browser)
    stepMs = 2 * 60 * 1000;
  } else if (orbitalPeriodMs > 2 * 60 * 60 * 1000) {
    // MEO: 30-second steps
    stepMs = 30 * 1000;
  } else {
    // LEO: 15-second steps
    stepMs = 15 * 1000;
  }

  if (!Number.isFinite(durationMs) || durationMs <= 0) return [];
  if (!Number.isFinite(stepMs) || stepMs <= 0) return [];

  if (MAX_GROUND_TRACK_SAMPLES < 2) return [];
  if (Math.floor(durationMs / stepMs) + 1 > MAX_GROUND_TRACK_SAMPLES) {
    stepMs = Math.ceil(durationMs / (MAX_GROUND_TRACK_SAMPLES - 1));
  }

  // ── Centre the track window around `start` ──
  // The satellite dot is drawn at `start`, so the ground track must
  // pass through that same moment.  Starting half the duration earlier
  // places the dot in the middle of the line.
  const windowStart = new Date(start.getTime() - durationMs / 2);

  const allCoords = propagateAllPoints(satrec, windowStart, durationMs, stepMs);
  if (allCoords.length < 2) return [];

  const name = record.name ?? 'Satellite';
  const norad = record.norad_cat_id ?? record.satelliteId ?? null;

  const subSegments = splitSegments(allCoords, stepMs);
  const features: GeoJSON.Feature[] = [];

  for (let i = 0; i < subSegments.length; i++) {
    const coordinates = subSegments[i];
    if (coordinates.length < 2) continue;
    features.push({
      type: 'Feature' as const,
      properties: { name, norad, segment: i },
      geometry: { type: 'LineString' as const, coordinates },
    });
  }

  return features;
}

export function tleRecordsToGroundTracksFeatureCollection(
  records: TleRecord[],
  start = new Date(),
  options: GroundTrackOptions = {}
): GeoJSON.FeatureCollection {
  const max = options.maxSatellites ?? 40;
  const features: GeoJSON.Feature[] = [];
  let includedSatelliteCount = 0;
  for (const tleRecord of records) {
    if (includedSatelliteCount >= max) break;
    if (!tleRecord.line1 || !tleRecord.line2) continue;
    const groundTrackFeatures = tleRecordToGroundTrackFeatures(tleRecord, start, options);
    if (groundTrackFeatures.length === 0) continue;
    features.push(...groundTrackFeatures);
    includedSatelliteCount += 1;
  }
  return { type: 'FeatureCollection', features };
}

/**
 * Parse orbital parameters from TLE line 1 and line 2.
 * Reference: https://en.wikipedia.org/wiki/Two-line_element_set
 */
export function parseTleOrbitalData(line1: string, line2: string): Record<string, unknown> {
  const MU = 398600.4418; // Earth GM, km³/s²
  const EARTH_RADIUS = 6371; // km

  try {
    // ── Line 1 fields ──
    const norad = parseInt(line1.substring(2, 7).trim(), 10);
    const classification = line1.charAt(7); // U = unclassified, C = classified, S = secret
    const intlDesignator = line1.substring(9, 17).trim(); // launch year + piece
    const epochYr = parseInt(line1.substring(18, 20).trim(), 10);
    const epochDay = parseFloat(line1.substring(20, 32).trim());
    const meanMotionDot = parseFloat(line1.substring(33, 43).trim()); // 1st derivative ÷ 2
    // BSTAR drag (implied decimal point: e.g. " 12345-3" → 0.12345e-3)
    const bstarRaw = line1.substring(53, 61).trim();
    let bstar = 0;
    if (bstarRaw.length > 0) {
      const mantissa = parseFloat('0.' + bstarRaw.substring(0, bstarRaw.length - 2).replace(/[+-\s]/g, ''));
      const exp = parseInt(bstarRaw.slice(-2), 10);
      bstar = (bstarRaw.charAt(0) === '-' ? -1 : 1) * mantissa * Math.pow(10, exp);
    }
    const elementSetNumber = parseInt(line1.substring(64, 68).trim(), 10);

    // ── Line 2 fields ──
    const inclination = parseFloat(line2.substring(8, 16).trim());
    const raan = parseFloat(line2.substring(17, 25).trim()); // Right Ascension of Ascending Node
    const eccentricity = parseFloat('0.' + line2.substring(26, 33).trim());
    const argOfPerigee = parseFloat(line2.substring(34, 42).trim());
    const meanAnomaly = parseFloat(line2.substring(43, 51).trim());
    const meanMotion = parseFloat(line2.substring(52, 63).trim()); // rev/day
    const revNumber = parseInt(line2.substring(63, 68).trim(), 10);

    // ── Derived values ──
    const periodMin = (24 * 60) / meanMotion;
    // Semi-major axis from mean motion: a = (μ / n²)^(1/3),  n in rad/s
    const nRadSec = (meanMotion * 2 * Math.PI) / 86400;
    const semiMajorAxis = Math.pow(MU / (nRadSec * nRadSec), 1 / 3); // km
    const apogeeKm = semiMajorAxis * (1 + eccentricity) - EARTH_RADIUS;
    const perigeeKm = semiMajorAxis * (1 - eccentricity) - EARTH_RADIUS;

    // Epoch as ISO date
    const fullYear = epochYr >= 57 ? 1900 + epochYr : 2000 + epochYr;
    const epochDate = new Date(Date.UTC(fullYear, 0, 1));
    epochDate.setTime(epochDate.getTime() + (epochDay - 1) * 86400000);

    // Classify orbit type
    let orbitType = 'LEO';
    if (periodMin > 1380) orbitType = 'GEO';
    else if (periodMin > 600) orbitType = 'HEO';
    else if (periodMin > 120) orbitType = 'MEO';

    return {
      norad,
      classification: classification === 'U' ? 'Unclassified' : classification === 'C' ? 'Classified' : classification === 'S' ? 'Secret' : classification,
      intlDesignator,
      epoch: epochDate.toISOString(),
      inclination: Math.round(inclination * 100) / 100,
      raan: Math.round(raan * 100) / 100,
      eccentricity: Math.round(eccentricity * 1e7) / 1e7,
      argOfPerigee: Math.round(argOfPerigee * 100) / 100,
      meanAnomaly: Math.round(meanAnomaly * 100) / 100,
      meanMotion: Math.round(meanMotion * 1e8) / 1e8,
      meanMotionDot,
      bstar,
      periodMin: Math.round(periodMin * 10) / 10,
      semiMajorAxis: Math.round(semiMajorAxis * 10) / 10,
      apogeeKm: Math.round(apogeeKm * 10) / 10,
      perigeeKm: Math.round(perigeeKm * 10) / 10,
      orbitType,
      revNumber: Number.isFinite(revNumber) ? revNumber : null,
      elementSetNumber: Number.isFinite(elementSetNumber) ? elementSetNumber : null,
    };
  } catch (error) {
    console.warn('[TLE] parseTleOrbitalData failed:', error);
    return {};
  }
}

export function tleRecordsToFeatureCollection(
  records: TleRecord[],
  date = new Date()
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const tleRecord of records) {
    if (!tleRecord.line1 || !tleRecord.line2) continue;
    const position = propagateLonLat(tleRecord.line1, tleRecord.line2, date);
    if (!position) continue;
    const [lon, lat] = position;
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;

    const orbital = parseTleOrbitalData(tleRecord.line1, tleRecord.line2);

    features.push({
      type: 'Feature',
      properties: {
        name: tleRecord.name ?? 'Satellite',
        norad: tleRecord.norad_cat_id ?? tleRecord.satelliteId ?? null,
        latitude: Math.round(lat * 1e5) / 1e5,
        longitude: Math.round(lon * 1e5) / 1e5,
        ...orbital,
      },
      geometry: { type: 'Point', coordinates: [lon, lat] },
    });
  }
  return { type: 'FeatureCollection', features };
}

export async function loadSatelliteFeatureCollection(): Promise<GeoJSON.FeatureCollection> {
  const members = await fetchAllTleMembers();
  return tleRecordsToFeatureCollection(members);
}